/**
 * useCompliancePolling Hook
 * ========================
 *
 * Polls for compliance check status after async file upload.
 * Automatically checks status every 2 seconds until compliance report is ready.
 *
 * Usage:
 * ```tsx
 * const { report, isPolling, error } = useCompliancePolling({
 *   fileId: uploadedFileId,
 *   enabled: uploadComplete,
 *   onComplete: (report) => console.log('Done!', report)
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import complianceService, { ComplianceReport } from '../services/complianceService';

interface UseCompliancePollingOptions {
  /** The uploaded file ID to poll for */
  fileId: string | null;
  /** Whether polling is enabled (set to true after upload) */
  enabled: boolean;
  /** Polling interval in milliseconds (default: 2000ms / 2 seconds) */
  pollInterval?: number;
  /** Maximum time to wait in milliseconds (default: 60000ms / 60 seconds) */
  maxWaitTime?: number;
  /** Callback when compliance check completes */
  onComplete?: (report: ComplianceReport) => void;
  /** Callback when polling encounters an error */
  onError?: (error: Error) => void;
  /** Callback when polling times out */
  onTimeout?: () => void;
}

interface UseCompliancePollingReturn {
  /** The compliance report (null until completed) */
  report: ComplianceReport | null;
  /** Whether actively polling */
  isPolling: boolean;
  /** Current status */
  status: 'idle' | 'in_progress' | 'completed' | 'not_found' | 'error' | 'timeout';
  /** Error if one occurred */
  error: Error | null;
  /** Manually trigger polling (usually not needed) */
  startPolling: () => void;
  /** Stop polling */
  stopPolling: () => void;
  /** Reset state */
  reset: () => void;
}

export function useCompliancePolling({
  fileId,
  enabled,
  pollInterval = 2000,
  maxWaitTime = 60000,
  onComplete,
  onError,
  onTimeout,
}: UseCompliancePollingOptions): UseCompliancePollingReturn {
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [status, setStatus] = useState<'idle' | 'in_progress' | 'completed' | 'not_found' | 'error' | 'timeout'>('idle');
  const [error, setError] = useState<Error | null>(null);

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setReport(null);
    setStatus('idle');
    setError(null);
    startTimeRef.current = null;
  }, [stopPolling]);

  const poll = useCallback(async () => {
    if (!fileId || !isMountedRef.current) {
      return;
    }

    // Check if we've exceeded max wait time
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= maxWaitTime) {
        console.warn(`[useCompliancePolling] Timeout after ${elapsed}ms`);
        stopPolling();
        setStatus('timeout');
        onTimeout?.();
        return;
      }
    }

    try {
      const result = await complianceService.getComplianceStatus(fileId);

      if (!isMountedRef.current) return;

      console.log(`[useCompliancePolling] Status: ${result.status}`, result);

      if (result.status === 'completed') {
        // Success! Compliance check complete
        console.log('📊 [useCompliancePolling] COMPLETED - Full result:', JSON.stringify(result, null, 2));
        console.log('📊 [useCompliancePolling] Report object:', result.report);
        console.log('📊 [useCompliancePolling] Report sanctions_check type:', typeof result.report?.sanctions_check);
        console.log('📊 [useCompliancePolling] Report sanctions_check is array:', Array.isArray(result.report?.sanctions_check));
        console.log('📊 [useCompliancePolling] Report sanctions_check length:', result.report?.sanctions_check?.length || 0);
        console.log('📊 [useCompliancePolling] Report overall_status:', result.report?.overall_status);

        setReport(result.report || null);
        setStatus('completed');
        stopPolling();

        if (result.report) {
          onComplete?.(result.report);
        }
        return;
      }

      if (result.status === 'not_found') {
        // No compliance check found
        console.warn('[useCompliancePolling] Compliance check not found');
        setStatus('not_found');
        stopPolling();
        return;
      }

      if (result.status === 'in_progress') {
        // Still processing, continue polling
        setStatus('in_progress');

        // Schedule next poll
        pollTimeoutRef.current = setTimeout(poll, pollInterval);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      const error = err instanceof Error ? err : new Error('Polling failed');
      console.error('[useCompliancePolling] Error:', error);

      setError(error);
      setStatus('error');
      stopPolling();
      onError?.(error);
    }
  }, [fileId, pollInterval, maxWaitTime, onComplete, onError, onTimeout, stopPolling]);

  const startPolling = useCallback(() => {
    if (!fileId || isPolling) {
      return;
    }

    console.log(`[useCompliancePolling] Starting polling for file ${fileId}`);

    setIsPolling(true);
    setStatus('in_progress');
    setError(null);
    startTimeRef.current = Date.now();

    // Start first poll immediately
    poll();
  }, [fileId, isPolling, poll]);

  // Auto-start polling when enabled and fileId is set
  useEffect(() => {
    if (enabled && fileId && !isPolling && status === 'idle') {
      startPolling();
    }
  }, [enabled, fileId, isPolling, status, startPolling]);

  // Cleanup on fileId change
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [fileId]);

  return {
    report,
    isPolling,
    status,
    error,
    startPolling,
    stopPolling,
    reset,
  };
}

export default useCompliancePolling;
